import { useMemo } from 'react'
import { useIngestions } from '@/hooks/useIngestions'

interface ActivitySparklineProps {
  days?: number
}

export default function ActivitySparkline({ days = 30 }: ActivitySparklineProps) {
  const { data: ingestions } = useIngestions()

  const activityData = useMemo(() => {
    if (!ingestions) return []

    const now = new Date()
    const dayMap = new Map<string, number>()

    // Initialize all days with 0
    for (let i = 0; i < days; i++) {
      const date = new Date(now)
      date.setDate(date.getDate() - i)
      const key = date.toISOString().split('T')[0]
      dayMap.set(key, 0)
    }

    // Count ingestions per day
    ingestions.forEach((ing) => {
      const date = new Date(ing.ingested_at).toISOString().split('T')[0]
      if (dayMap.has(date)) {
        dayMap.set(date, (dayMap.get(date) || 0) + 1)
      }
    })

    // Convert to array, oldest first
    return Array.from(dayMap.entries())
      .sort((a, b) => a[0].localeCompare(b[0]))
      .map(([, count]) => count)
  }, [ingestions, days])

  const maxActivity = Math.max(...activityData, 1)

  if (activityData.length === 0) {
    return (
      <div className="h-12 flex items-center justify-center text-xs text-white/30">
        Sem dados de atividade
      </div>
    )
  }

  const startDate = new Date()
  startDate.setDate(startDate.getDate() - days + 1)
  const endDate = new Date()

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between text-[10px] text-white/40">
        <span>{startDate.toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' })}</span>
        <span>Atividade (últimos {days} dias)</span>
        <span>{endDate.toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' })}</span>
      </div>

      <div className="h-10 flex items-end gap-[2px]">
        {activityData.map((count, index) => {
          const height = count > 0 ? Math.max(15, (count / maxActivity) * 100) : 5
          const opacity = count > 0 ? 0.4 + (count / maxActivity) * 0.6 : 0.1

          return (
            <div
              key={index}
              className="flex-1 rounded-t transition-all duration-200 hover:opacity-100"
              style={{
                height: `${height}%`,
                backgroundColor: count > 0 ? `rgba(124, 58, 237, ${opacity})` : 'rgba(255,255,255,0.05)',
              }}
              title={`${count} ingestões`}
            />
          )
        })}
      </div>
    </div>
  )
}
